import { Box, Link, Typography } from '@mui/material'
import { useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { type StatusIndicatorValue } from '@omnea-app/frontend/utils/hooks/useSystemStatus'

type StatusIndicatorProps = {
  indicator: StatusIndicatorValue
  description: string
  statusPageUrl: string
  ariaLabel: string
}

const STATUS_COLOR = {
  none: 'success.500',
  minor: 'warning.500',
  major: 'danger.main',
  critical: 'error.main',
} satisfies Record<StatusIndicatorValue, string>

export const StatusIndicator = ({ indicator, description, statusPageUrl, ariaLabel }: StatusIndicatorProps) => {
  const isMobile = useIsMobileWidth()

  // On mobile, only show the description when there's an active incident —
  // the dot alone is sufficient when everything is operational.
  const displayText = isMobile ? (indicator !== 'none' ? description : null) : description

  return (
    <Link
      href={statusPageUrl}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transition: 'opacity 0.2s ease-in-out',
        '&:hover': { opacity: 0.7 },
      }}
    >
      <Box
        component="span"
        aria-hidden={true}
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: STATUS_COLOR[indicator],
          flexShrink: 0,
        }}
      />
      {displayText && (
        <Typography
          variant="Body12pxRegular"
          sx={{
            fontSize: isMobile ? '10px' : '12px',
            color: 'neutralLight.900',
            lineHeight: 1.2,
          }}
        >
          {displayText}
        </Typography>
      )}
    </Link>
  )
}
